import type { StoreItem } from '../types/get-store-items';

/**
 * Promoção resolvida de um produto: o preço cheio ("De") e o preço de venda
 * promocional ("Por"), ambos já normalizados como número.
 */
export type Promotion = {
  /** Preço cheio/original exibido riscado ("De"). */
  regularPrice: number;
  /** Preço de venda promocional cobrado no checkout ("Por"). */
  salePrice: number;
};

// Teto defensivo para descartar valores absurdos vindos do backend (ex.:
// overflow, dado corrompido). Preços de itens de cardápio ficam muito abaixo
// disso; qualquer coisa acima é tratada como inválida (default seguro).
const MAX_REASONABLE_PRICE = 1_000_000;

/**
 * Converte um valor de preço (string do backend ou número) para número de forma
 * defensiva. Aceita formatos como "25.00", "25,00" e "R$ 25,00". Retorna `null`
 * quando o valor é ausente, não numérico, negativo, não finito ou fora de um
 * intervalo razoável — impedindo que valores inválidos cheguem ao cálculo de
 * preço e, por consequência, ao checkout (OWASP A04: Insecure Design).
 */
export function parsePrice(value: unknown): number | null {
  if (typeof value === 'number') {
    return Number.isFinite(value) && value >= 0 && value <= MAX_REASONABLE_PRICE ? value : null;
  }

  if (typeof value !== 'string') return null;

  // Mantém apenas dígitos, separadores e sinal; remove símbolos monetários/espaços.
  const cleaned = value.replace(/[^\d.,-]/g, '').trim();
  if (!cleaned) return null;

  // Normaliza o separador decimal: quando só há vírgula, ela é o decimal
  // (padrão pt-BR). Havendo ponto, assume-se ponto como decimal e a vírgula
  // como separador de milhar.
  const normalized = cleaned.includes('.')
    ? cleaned.replace(/,/g, '')
    : cleaned.replace(',', '.');

  const parsed = Number(normalized);
  if (!Number.isFinite(parsed) || parsed < 0 || parsed > MAX_REASONABLE_PRICE) {
    return null;
  }

  return parsed;
}

/**
 * Retorna a promoção do produto quando ela é válida, ou `null` caso contrário.
 *
 * O campo `discount` é o **valor abatido** do preço cheio (quanto se desconta),
 * não o preço final. Portanto o preço de venda ("Por") é sempre
 * `preço cheio − desconto`. Uma promoção só é considerada válida quando esse
 * abatimento é real, ou seja, quando o desconto é finito, maior que zero e
 * menor que o preço cheio — resultando num preço de venda positivo e
 * estritamente menor que o cheio. Isso evita exibir "promoções" que na verdade
 * encarecem ou zeram o item, ou que trazem valores inválidos/negativos
 * (default seguro).
 */
export function getPromotion(
  item: Pick<StoreItem, 'price' | 'discount'> | null | undefined
): Promotion | null {
  if (!item) return null;

  const regularPrice = parsePrice(item.price);
  const discountAmount = parsePrice(item.discount);

  if (regularPrice === null || discountAmount === null) return null;

  // "Por" = preço cheio − valor do desconto. Arredonda para centavos para não
  // propagar imprecisão de ponto flutuante ao valor levado ao checkout.
  const salePrice = Math.round((regularPrice - discountAmount) * 100) / 100;
  if (salePrice <= 0 || salePrice >= regularPrice) return null;

  return { regularPrice, salePrice };
}

/**
 * Preço efetivo do produto (base, sem adicionais): o preço de venda quando há
 * promoção válida, senão o preço cheio. Sempre retorna um número não negativo
 * — é este valor que deve ser levado ao carrinho/checkout.
 */
export function getEffectivePrice(
  item: Pick<StoreItem, 'price' | 'discount'> | null | undefined
): number {
  if (!item) return 0;

  const promotion = getPromotion(item);
  if (promotion) return promotion.salePrice;

  return parsePrice(item.price) ?? 0;
}

/**
 * Percentual de desconto da promoção ("De/Por"), arredondado para inteiro.
 *
 * Calculado a partir dos preços já validados por {@link getPromotion}, então é
 * sempre finito. Ainda assim, o resultado é limitado defensivamente ao intervalo
 * 1–99 (default seguro): como o preço de venda é estritamente menor que o cheio
 * e ambos são positivos, o percentual real está sempre nesse intervalo, mas o
 * clamp protege a UI contra qualquer arredondamento/dado de borda. Retorna
 * `null` quando não há promoção válida.
 */
export function getDiscountPercent(
  item: Pick<StoreItem, 'price' | 'discount'> | null | undefined
): number | null {
  const promotion = getPromotion(item);
  if (!promotion) return null;

  const percent = Math.round(
    ((promotion.regularPrice - promotion.salePrice) / promotion.regularPrice) * 100
  );

  if (!Number.isFinite(percent) || percent <= 0) return null;

  return Math.min(percent, 99);
}

/** `true` quando o produto tem uma promoção válida (preço de venda < preço cheio). */
export function isOnPromotion(
  item: Pick<StoreItem, 'price' | 'discount'> | null | undefined
): boolean {
  return getPromotion(item) !== null;
}
